import { login } from "@lib/data/customer"
import { LOGIN_VIEW } from "@modules/account/templates/login-template"
import ErrorMessage from "@modules/checkout/components/error-message"
import { SubmitButton } from "@modules/checkout/components/submit-button"
import Input from "@modules/common/components/input"
import { sdk } from "@lib/sdk"
import { useState } from "react"
import { useActionState } from "react"

type Props = {
  setCurrentView: (view: LOGIN_VIEW) => void
}

const Login = ({ setCurrentView }: Props) => {
  const [message, formAction] = useActionState(login, null)
  const [googleLoading, setGoogleLoading] = useState(false)

  const goToReset = () => {
    window.location.href =
      "https://www.farmaciapaseo51.com/es/account/request-reset-password"
  }

  const loginWithGoogle = async () => {
    setGoogleLoading(true)
    try {
      const result = await sdk.auth.login("customer", "google", {})

      if (typeof result === "object" && result.location) {
        window.location.href = result.location
        return
      }

      if (typeof result !== "string") {
        alert("Authentication failed")
        setGoogleLoading(false)
        return
      }

      // Customer previously authenticated
      window.location.href = "/"
    } catch (error) {
      console.error("Google login error:", error)
      setGoogleLoading(false)
    }
  }

  return (
    <div
      className="max-w-sm w-full flex flex-col items-center"
      data-testid="login-page"
    >
      <h1 className="text-large-semi uppercase mb-6">Bienvenido de nuevo</h1>
      <p className="text-center text-base-regular text-ui-fg-base mb-8">
        Inicia sesión para disfrutar de una mejor experiencia de compra.
      </p>

      <form className="w-full" action={formAction}>
        <div className="flex flex-col w-full gap-y-2">
          <Input
            label="Correo electrónico"
            name="email"
            type="email"
            title="Ingresa un correo electrónico válido."
            autoComplete="email"
            required
            data-testid="email-input"
          />
          <Input
            label="Contraseña"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            data-testid="password-input"
          />
        </div>

        {/* Enlace para resetear contraseña */}
        <div className="w-full flex justify-end mt-2">
          <button
            type="button"
            onClick={goToReset}
            className="text-small-regular underline text-ui-fg-base hover:opacity-80"
            data-testid="forgot-password-button"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        <ErrorMessage error={message} data-testid="login-error-message" />

        <SubmitButton data-testid="sign-in-button" className="w-full mt-6">
          Iniciar sesión
        </SubmitButton>
      </form>

      <div className="w-full flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-ui-border-base" />
        <span className="text-small-regular text-ui-fg-base">o</span>
        <div className="flex-1 h-px bg-ui-border-base" />
      </div>

      {/* Google Login Button */}
      <button
        onClick={loginWithGoogle}
        disabled={googleLoading}
        className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-ui-border-base rounded-full hover:bg-ui-bg-highlight hover:border-ui-border-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="google-login-button"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span className="text-small-regular font-medium text-ui-fg-base">
          {googleLoading ? "Cargando..." : "Continuar con Google"}
        </span>
      </button>

      <span className="text-center text-ui-fg-base text-small-regular mt-6">
        ¿No tienes cuenta?{" "}
        <button
          onClick={() => setCurrentView(LOGIN_VIEW.REGISTER)}
          className="underline"
          data-testid="register-button"
        >
          Regístrate
        </button>
        .
      </span>
    </div>
  )
}

export default Login
